import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { API } from '../utils/constants';
import RecipeForm from './RecipeForm';
import recipeCache from '../utils/recipeCache';

/**
 * Save-flow v3: the save-edit screen is an overlay above the page you're on,
 * over the page it came from. Collection saves retain the soft blurred
 * backdrop; individual recipe saves use a solid cream backdrop so the form
 * does not compete with the recipe image or creator details underneath.
 *
 * - Pre-filled with the whole recipe; only a category is required
 *   (suggested from the saver's own categories — no AI).
 * - Nothing is created until Save: the form posts category + edits to
 *   /recipes/{id}/save in one shot.
 * - Cancel/close just dismisses the overlay — you're still on the exact
 *   spot of the other person's page, nothing saved.
 * - Save lands on YOUR OWN Wellby scrolled to the new card (highlighted).
 */
const SaveRecipeOverlay = ({ original, onClose, solidBackdrop = false }) => {
  const navigate = useNavigate();
  const [adminToken] = useState(() => localStorage.getItem('adminToken') || '');
  const [profile, setProfile] = useState(null);
  const [categories, setCategories] = useState([]);
  const [suggestedCategory, setSuggestedCategory] = useState(null);
  const [ready, setReady] = useState(false);

  // Keep the page underneath from scrolling while the overlay is up.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  const fetchCategories = async (token = adminToken) => {
    try {
      const res = await axios.get(`${API}/categories`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const sorted = (res.data || []).slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      setCategories(sorted);
      return sorted;
    } catch {
      return [];
    }
  };

  useEffect(() => {
    if (!original || !adminToken) return;
    let cancelled = false;
    (async () => {
      const [profileRes, owned] = await Promise.all([
        axios
          .get(`${API}/my-profile`, { headers: { Authorization: `Bearer ${adminToken}` } })
          .catch(() => null),
        fetchCategories(),
      ]);
      if (cancelled) return;
      if (profileRes) setProfile(profileRes.data);

      // Pre-fill the category from the saver's own categories (no AI).
      // Best-effort — an empty suggestion just means the saver picks
      // manually (still required by the form).
      if (owned.length > 0) {
        try {
          const res = await axios.post(
            `${API}/suggest-save-category`,
            {
              title: original.title || '',
              description: original.description || '',
              original_categories: original.categories || [],
            },
            { headers: { Authorization: `Bearer ${adminToken}` } }
          );
          if (!cancelled && res.data?.category) setSuggestedCategory(res.data.category);
        } catch {
          /* no suggestion — user picks manually */
        }
      }
      if (!cancelled) setReady(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [original?.id, adminToken]);

  if (!original || !adminToken) return null;

  const handleSaved = (savedRecipe) => {
    recipeCache.invalidateLists();
    const username =
      profile?.username || JSON.parse(localStorage.getItem('user') || '{}').username;
    navigate(username ? `/${encodeURIComponent(username)}` : '/recipes', {
      state: { highlightRecipeId: savedRecipe?.id },
    });
  };

  return (
    <div
      className={`fixed inset-0 z-50 overflow-y-auto ${
        solidBackdrop ? 'bg-[#EEE7D8]' : 'bg-[#EEE7D8]/60 backdrop-blur-sm'
      }`}
      data-testid="save-recipe-overlay"
      /* No backdrop-click dismissal: a stray click must never throw away
         in-progress edits. Closing is explicit via the form's Cancel. */
    >
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Kept intentionally generic — showing the user's own Wellby name
            here made it read like the recipe was already theirs. */}
        <div className="mx-8 mb-6 flex items-center gap-1.5" data-testid="save-recipe-header">
          <span
            className="text-base"
            style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}
            data-testid="saving-to-your-wellby"
          >
            Saving to your Wellby
          </span>
        </div>

        <Card className="overflow-hidden" style={{ backgroundColor: '#F3EEE4', boxShadow: 'none', border: '1px solid #D6D0C2' }}>
          <CardContent className="p-8 recipe-edit-form">
            {!ready ? (
              <p style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}>
                Getting this recipe ready to save…
              </p>
            ) : (
              <RecipeForm
                recipe={{
                  ...original,
                  id: undefined, // not editable in place — created via /save
                  categories: suggestedCategory ? [suggestedCategory] : [],
                  notes: '',
                  is_public: true,
                }}
                saveFromRecipe={original}
                onSave={handleSaved}
                onCancel={onClose}
                adminToken={adminToken}
                isEditing={false}
                submitLabel="Save"
                categories={categories}
                onManageCategories={fetchCategories}
              />
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SaveRecipeOverlay;
